import { ACTION_STATES, CharacterState, LAST_KNOWN_STATE, characterStateOf, stripOfState } from "../combat/anim.ts";
import { HARVEST_STRIP } from "./harvest-strip.ts";
import type { SheetAnim } from "./sheet.ts";

/**
 * Which strip a character plays beyond idle and walk (ADR-074 decision 7, ADR-036's amendment for P2.1-T4): a
 * one-shot strip playing out, or a held one on its frames and then its last. A character drawn with no strip of its
 * own here is drawn by its motion.
 *
 * Another character's strips follow the animation bits its snapshots carry (`observe`): a change of the byte plays
 * the state's strip, the restart bit alone counting as a change, and first sight plays nothing but draws the dead
 * lying (ADR-058's first-sight rule applied to characters). The page's own character plays from intent and its own
 * events instead (`play`, `hurt`, `hold`, `clear`). A hurt waits for a swing that plays to finish and is dropped,
 * since the server's priority covers only one tick. Nothing here allocates: the state is a few fields read each frame.
 *
 * @example
 * ```ts
 * playback.observe(state.anim, now);
 * if (playback.resolve(sheet.anims, now)) {
 *   show(playback.strip, playback.frame);
 * }
 * ```
 */
export class CharacterPlayback {
  private playing: string | null = null;
  private startMs = 0;
  private holding = false;
  private repeating = false;
  private shown = 0;
  private lastByte = -1;
  private started = 0;

  /**
   * How many strips have started, each `play` and `hold` one more, so a drawing can tell a new strip from the one it
   * drew last frame, even the same strip started again (the drawn character keeps a swing's direction for its length,
   * ADR-073's amendment "the way a displacement goes, and a swing keeps its facing").
   *
   * @returns The count.
   */
  public get starts(): number {
    return this.started;
  }

  /**
   * The strip shown by the last `resolve` that returned true.
   *
   * @returns The strip's name, or null when motion decides.
   */
  public get strip(): string | null {
    return this.playing;
  }

  /**
   * The frame of `strip` the last `resolve` chose.
   *
   * @returns The frame index.
   */
  public get frame(): number {
    return this.shown;
  }

  /**
   * Whether the strip is held on its last frame once played, as the death strip is.
   *
   * @returns True while a held strip is shown.
   */
  public get held(): boolean {
    return this.playing !== null && this.holding;
  }

  /**
   * Plays a strip once from now, cutting short whatever played.
   *
   * @param strip The strip.
   * @param nowMs Scene time in ms.
   */
  public play(strip: string, nowMs: number): void {
    this.started += 1;
    this.playing = strip;
    this.startMs = nowMs;
    this.holding = false;
    this.repeating = false;
  }

  /**
   * Plays a strip over and over from now until `clear` or another strip: the harvest's slash, which lasts as long
   * as the hold does (ADR-142's amendment of 2026-10-31).
   *
   * @param strip The strip.
   * @param nowMs Scene time in ms.
   */
  public loop(strip: string, nowMs: number): void {
    this.play(strip, nowMs);
    this.repeating = true;
  }

  /**
   * Whether the strip shown repeats until cleared.
   *
   * @returns True while a looping strip shows.
   */
  public get looping(): boolean {
    return this.playing !== null && this.repeating;
  }

  /**
   * Swaps the strip playing for another of the same length from the same start, so the frame shown keeps its place:
   * the page's own swing turned to the strip the server chose (ADR-073's amendment "the Strike combo and the Heavy
   * Strike chain", item 8). It counts no new start.
   *
   * @param from The strip expected to be playing.
   * @param to The strip to play in its place.
   * @returns True when `from` was playing and is swapped.
   */
  public swap(from: string, to: string): boolean {
    if (this.playing !== from || this.holding) {
      return false;
    }
    this.playing = to;
    return true;
  }

  /**
   * Plays a strip once and then holds its last frame while nothing else is asked, or shows its last frame at once.
   *
   * @param strip The strip.
   * @param nowMs Scene time in ms.
   * @param atEnd Whether to start on the last frame, as a character first seen dead is drawn lying.
   */
  public hold(strip: string, nowMs: number, atEnd: boolean): void {
    this.started += 1;
    this.playing = strip;
    this.startMs = atEnd ? Number.NEGATIVE_INFINITY : nowMs;
    this.holding = true;
    this.repeating = false;
  }

  /**
   * Plays the hurt strip, unless a held strip shows or a swing (a strip an ability names) is playing, which the hurt
   * lets finish and does not follow.
   *
   * @param nowMs Scene time in ms.
   */
  public hurt(nowMs: number): void {
    if (this.playing !== null && (this.holding || ACTION_STATES.has(this.playing))) {
      return;
    }
    this.play("hurt", nowMs);
  }

  /**
   * Stops whatever plays, handing the character back to its motion.
   */
  public clear(): void {
    this.playing = null;
    this.holding = false;
    this.repeating = false;
  }

  /**
   * Forgets the last byte observed, so the next is first sight.
   */
  public forget(): void {
    this.lastByte = -1;
  }

  /**
   * Reads the animation bits a character's snapshot carries this frame.
   *
   * @param anim The six animation bits.
   * @param nowMs Scene time in ms.
   * @returns False when the byte carries a state the page does not know, which draws nothing new and which the caller
   *   may log once.
   */
  public observe(anim: number, nowMs: number): boolean {
    const state = characterStateOf(anim);
    const known = state <= LAST_KNOWN_STATE;
    if (this.lastByte < 0) {
      this.lastByte = anim;
      if (state === CharacterState.Dead) {
        this.hold("death", nowMs, true);
      } else if (state === CharacterState.Harvest) {
        this.loop(HARVEST_STRIP, nowMs);
      }
      return known;
    }
    if (anim === this.lastByte) {
      return known;
    }
    this.lastByte = anim;
    if (state === CharacterState.Dead) {
      this.hold("death", nowMs, false);
    } else if (state === CharacterState.None) {
      this.clear();
    } else if (state === CharacterState.Hurt) {
      this.hurt(nowMs);
    } else if (state === CharacterState.Harvest) {
      this.loop(HARVEST_STRIP, nowMs);
    } else {
      const strip = stripOfState(state);
      if (strip !== null) {
        this.play(strip, nowMs);
      }
    }
    return known;
  }

  /**
   * Chooses the frame to show now: a one-shot strip's frame until its last has shown, then motion; a held strip's
   * frame, stopping on its last; a looping strip's frame, round again. A strip the character's sheet lacks is dropped for motion.
   *
   * @param anims The character's sheet's strips.
   * @param nowMs Scene time in ms.
   * @returns True when `strip` and `frame` name what to show; false when motion decides.
   */
  public resolve(anims: Readonly<Record<string, SheetAnim>>, nowMs: number): boolean {
    const playing = this.playing;
    if (playing === null) {
      return false;
    }
    const anim = anims[playing];
    if (anim === undefined) {
      this.clear();
      return false;
    }
    const elapsed = nowMs - this.startMs;
    const index = elapsed === Number.POSITIVE_INFINITY ? anim.frames : Math.floor((Math.max(0, elapsed) * anim.fps) / 1000);
    if (this.repeating) {
      this.shown = index % anim.frames;
      return true;
    }
    if (index < anim.frames) {
      this.shown = index;
      return true;
    }
    if (this.holding) {
      this.shown = anim.frames - 1;
      return true;
    }
    this.clear();
    return false;
  }
}
